import { useEffect, useRef, useState, type FormEvent } from "react";
import { Mic, Send, Loader2 } from "lucide-react";

import type { VoiceRecordingMeta } from "../lib/voice_perf";

interface AgentInputProps {
  onSend: (text: string) => Promise<unknown> | void;
  onSendAudio?: (
    blob: Blob,
    mimeType?: string,
    meta?: VoiceRecordingMeta,
  ) => Promise<unknown> | void;
  disabled?: boolean;
  pending?: boolean;
  placeholder?: string;
  /** Optional pre-fill from a suggestion pill. The component watches
   *  `prefillNonce` (not the value itself) so tapping the same pill
   *  twice still re-applies the prefill. */
  prefillValue?: string;
  prefillNonce?: number;
  agentError?: string | null;
}

/**
 * "fenêtre de dialogue avec l'agent" from interface_26_2.pdf — the
 * black mic button on the left + a long input + a send arrow.
 * Mic is a placeholder for v1 (push-to-talk in a later phase).
 */
export function AgentInput({
  onSend,
  onSendAudio,
  disabled,
  pending,
  placeholder = "Décrivez ce que vous voulez enregistrer…",
  prefillValue,
  prefillNonce,
  agentError,
}: AgentInputProps) {
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const interactionStartedAtRef = useRef<number | null>(null);

  // Adopt a fresh prefill from a suggestion pill, then focus the
  // input with the cursor at the end so the user can keep typing.
  useEffect(() => {
    if (prefillNonce === undefined || !prefillValue) return;
    setText(prefillValue);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const end = prefillValue.length;
      el.setSelectionRange(end, end);
    });
  }, [prefillNonce, prefillValue]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || disabled || pending) return;
    setText("");
    await onSend(value);
  };

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const startRecording = async () => {
    if (!onSendAudio || disabled || pending || recording) return;
    interactionStartedAtRef.current = nowMs();
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      interactionStartedAtRef.current = null;
      setMicError("Le micro n'est pas disponible sur cet appareil.");
      return;
    }
    try {
      setMicError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        const recordingEndedAt = nowMs();
        const mimeType =
          recorder.mimeType || chunksRef.current[0]?.type || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const interactionStartedAt = interactionStartedAtRef.current ?? recordingEndedAt;
        const meta: VoiceRecordingMeta = {
          interactionStartedAt,
          recordingStartedAt: interactionStartedAt,
          recordingEndedAt,
          recordingMs: Math.max(0, Math.round(recordingEndedAt - interactionStartedAt)),
        };
        chunksRef.current = [];
        recorderRef.current = null;
        interactionStartedAtRef.current = null;
        setRecording(false);
        stopTracks();
        if (blob.size > 0) {
          await onSendAudio(blob, mimeType, meta);
        }
      };
      recorder.start();
      setRecording(true);
    } catch {
      setRecording(false);
      interactionStartedAtRef.current = null;
      stopTracks();
      setMicError("Impossible d'accéder au micro.");
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    recorder.stop();
  };

  return (
    <div className="w-full">
      <form
        onSubmit={submit}
        className="flex items-center gap-3 w-full"
        role="search"
        aria-label="Dialogue avec l'agent"
      >
        <button
          type="button"
          title={recording ? "Relâchez pour envoyer" : "Maintenez pour parler"}
          className={[
            "h-12 w-12 shrink-0 rounded-full flex items-center justify-center transition",
            recording
              ? "bg-chip-orange text-white shadow-sm"
              : "bg-ink text-white hover:bg-ink/90",
            disabled || pending ? "opacity-40 cursor-not-allowed" : "",
          ].join(" ")}
          aria-label={recording ? "Micro en enregistrement" : "Micro"}
          aria-pressed={recording}
          disabled={disabled || pending}
          onPointerDown={() => {
            void startRecording();
          }}
          onPointerUp={stopRecording}
          onPointerCancel={stopRecording}
          onPointerLeave={() => {
            if (recording) stopRecording();
          }}
        >
          <Mic className="h-5 w-5" />
        </button>

        <div className="flex flex-1 items-center bg-white dark:bg-surface-dark rounded-full ring-1 ring-black/10 dark:ring-white/10 shadow-sm focus-within:ring-2 focus-within:ring-ink/40 dark:focus-within:ring-white/40">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={recording ? "Relâchez pour envoyer la dictée…" : placeholder}
            disabled={disabled || pending || recording}
            className="bun-textbox flex-1 px-5 py-3 text-base bg-transparent outline-none disabled:opacity-50"
            aria-label="Message à l'agent"
          />
          <button
            type="submit"
            disabled={disabled || pending || recording || !text.trim()}
            className="h-9 w-9 mr-1.5 rounded-full bg-ink text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-ink/90 transition"
            aria-label="Envoyer"
          >
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>
      {micError && (
        <p className="mt-2 text-xs text-chip-red" role="status" aria-live="polite">
          {micError}
        </p>
      )}
      {agentError && (
        <p className="mt-2 text-xs text-chip-red" role="alert">
          {agentError}
        </p>
      )}
    </div>
  );
}


function nowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}
